// src/services/accountService.ts
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  serverTimestamp,
  Timestamp,
  addDoc
} from 'firebase/firestore';
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification, getAuth, updateCurrentUser } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { createVenue } from './venueService';
import { VenueFormData, VenueType } from '../types/Venue';
import { bypassEmailVerificationForTesting } from '../firebase/testUtils';

// Account types
export type AccountType = 'personal' | 'venue' | 'organization';

// Account roles
export type AccountRole = 'owner' | 'admin' | 'manager' | 'staff';

// Account interface
export interface Account {
  id: string;
  type: AccountType;
  ownerId: string;
  name: string;
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
  status: 'active' | 'pending' | 'disabled';
  stripe?: {
    customerId?: string;
    accountId?: string;
    connected: boolean;
  };
  subscription?: {
    plan: string;
    status: string;
    currentPeriodEnd: Timestamp | null;
  };
}

// Account access interface
export interface AccountAccess {
  userId: string;
  accountId: string;
  role: AccountRole;
  permissions: {
    [key: string]: boolean;
  };
  createdAt: Timestamp | null;
  updatedAt: Timestamp | null;
}

// Create a venue account
export const createVenueAccount = async (accountData: any): Promise<any> => {
  try {
    let userId = accountData.userId;
    let userObj = null;
    
    // If the user doesn't exist and we have email/password, create a new user
    if (!userId && accountData.email && accountData.password) {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        accountData.email,
        accountData.password
      );
      
      userId = userCredential.user.uid;
      userObj = userCredential.user;
      
      // Update the user profile with the provided name
      if (accountData.name) {
        await updateProfile(userCredential.user, {
          displayName: accountData.name
        });
      }
      
      // For testing purposes: Skip email verification and set user as verified
      // Note: This is only for development/testing and should be removed in production
      try {
        // Bypass email verification for testing
        await bypassEmailVerificationForTesting(userCredential.user);
        
        // Still send the verification email as a fallback
        await sendEmailVerification(userCredential.user);
        console.log('Verification email sent, but bypassed for testing');
      } catch (verificationError) {
        console.error("Error with verification:", verificationError);
      }
    } else if (!userId) {
      throw new Error('Cannot create venue account: user ID or credentials are required');
    }
    
    // Check if user already exists and has existing accounts
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      // Create user document if it doesn't exist
      await setDoc(doc(db, 'users', userId), {
        displayName: accountData.name || '',
        email: accountData.email || '',
        phoneNumber: accountData.phone || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        settings: {
          allowNotifications: true
        }
      });
    }
    
    // Create a new account document
    const accountRef = doc(collection(db, 'accounts'));
    const accountId = accountRef.id;
    
    // Prepare the account data
    const newAccount: Omit<Account, 'id'> = {
      type: 'venue',
      ownerId: userId,
      name: accountData.businessName || accountData.venueName || 'Venue Account',
      createdAt: serverTimestamp() as Timestamp,
      updatedAt: serverTimestamp() as Timestamp,
      status: 'active',
      stripe: {
        accountId: accountData.stripeAccountId || undefined,
        connected: accountData.stripeConnected || false
      }
    };
    
    // Store the account data
    await setDoc(accountRef, newAccount);
    
    // Check if user already has a personal account
    // If not, create one automatically to ensure they have both types
    const userAccountsQuery = query(
      collection(db, 'accounts'),
      where('ownerId', '==', userId),
      where('type', '==', 'personal')
    );
    const userAccountsSnapshot = await getDocs(userAccountsQuery);
    
    let personalAccountId: string | null = null;
    
    if (userAccountsSnapshot.empty) {
      // Create a personal account for this user
      const personalAccountRef = doc(collection(db, 'accounts'));
      personalAccountId = personalAccountRef.id;
      
      const personalAccount: Omit<Account, 'id'> = {
        type: 'personal',
        ownerId: userId,
        name: `${accountData.name || 'User'}'s Personal Account`,
        createdAt: serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp,
        status: 'active'
      };
      
      await setDoc(personalAccountRef, personalAccount);
      console.log('Created personal account for venue owner:', personalAccountId);
    } else {
      personalAccountId = userAccountsSnapshot.docs[0].id;
    }
    
    // Create account access for the owner
    await setDoc(doc(db, 'userAccountAccess', `${userId}_${accountId}`), {
      userId,
      accountId,
      role: 'owner',
      permissions: {
        manageVenue: true,
        managePackages: true,
        manageBookings: true,
        managePayments: true,
        manageUsers: true
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Create venue with the account ID
    const venueData: VenueFormData = {
      name: accountData.venueName || accountData.businessName,
      type: accountData.venueType as VenueType || 'restaurant',
      description: accountData.venueDescription || '',
      contact: {
        email: accountData.email,
        phone: accountData.phone,
        website: accountData.website || ''
      },
      location: {
        address: accountData.businessAddress.street,
        city: accountData.businessAddress.city,
        state: accountData.businessAddress.state,
        postalCode: accountData.businessAddress.zip,
        country: accountData.businessAddress.country,
        coordinates: {
          latitude: 0, // These would be set using geocoding
          longitude: 0
        }
      },
      businessHours: Object.entries(accountData.businessHours || {}).map(
        ([day, hours]: [string, any]) => ({
          dayOfWeek: parseInt(day),
          open: hours.open,
          close: hours.close,
          isClosed: false
        })
      ),
      amenities: [],
      socialMedia: {
        facebook: '',
        instagram: '',
        twitter: '',
        linkedin: ''
      },
      images: {
        primary: '',
        gallery: [],
        logo: ''
      }
    };
    
    const venueId = await createVenue(userId, venueData);
    
    // Link the venue to the account
    await updateDoc(accountRef, {
      venueId,
      'updatedAt': serverTimestamp()
    });
    
    // Return the created account and venue IDs
    return {
      accountId,
      venueId,
      userId
    };
  } catch (error) {
    console.error('Error creating venue account:', error);
    throw error;
  }
};

// Get user accounts
export const getUserAccounts = async (userId: string): Promise<Account[]> => {
  try {
    // Query the userAccountAccess collection for the user
    const accessQuery = query(
      collection(db, 'userAccountAccess'),
      where('userId', '==', userId)
    );
    
    const accessSnapshot = await getDocs(accessQuery);
    const accountIds: string[] = [];
    
    accessSnapshot.forEach((doc) => {
      const accessData = doc.data() as AccountAccess;
      accountIds.push(accessData.accountId);
    });
    
    // Get the accounts data
    const accounts: Account[] = [];
    
    for (const accountId of accountIds) {
      const accountDoc = await getDoc(doc(db, 'accounts', accountId));
      
      if (accountDoc.exists()) {
        accounts.push({
          id: accountDoc.id,
          ...accountDoc.data()
        } as Account);
      }
    }
    
    return accounts;
  } catch (error) {
    console.error('Error getting user accounts:', error);
    throw error;
  }
};

// Update account
export const updateAccount = async (accountId: string, data: Partial<Account>): Promise<void> => {
  try {
    const accountRef = doc(db, 'accounts', accountId);
    
    // Add updatedAt timestamp
    const updatedData = {
      ...data,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(accountRef, updatedData);
  } catch (error) {
    console.error('Error updating account:', error);
    throw error;
  }
};

// Switch active account for user
export const setActiveAccount = async (userId: string, accountId: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', userId);
    
    await updateDoc(userRef, {
      activeAccountId: accountId,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error setting active account:', error);
    throw error;
  }
};

// Get account by ID
export const getAccountById = async (accountId: string): Promise<Account | null> => {
  try {
    const accountDoc = await getDoc(doc(db, 'accounts', accountId));
    
    if (accountDoc.exists()) {
      return {
        id: accountDoc.id,
        ...accountDoc.data()
      } as Account;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting account:', error);
    throw error;
  }
};
